import { notification } from "antd";

export const ADMIN_ROLES = [
  "super_admin",
  "admin",
  "moderator",
  "support",
  "finance",
  "legal_review",
];

export const canViewAdminArea = (role, area) => {
  const permissions = {
    users: ["super_admin", "admin", "support", "moderator"],
    pendingDeletions: ["super_admin", "admin", "support"],
    wills: ["super_admin", "admin", "support", "legal_review", "moderator"],
    designations: ["super_admin", "admin", "support"],
    transactions: ["super_admin", "admin", "finance"],
    auditLogs: ["super_admin"],
    plans: ["super_admin", "admin"],
  };

  if (!role) return area !== "auditLogs";
  return permissions[area]?.includes(role) ?? true;
};

export const canPerformAdminAction = (role, action) => {
  const permissions = {
    deleteUser: ["super_admin", "admin"],
    export: ["super_admin", "admin", "finance"],
    userStatus: ["super_admin", "admin", "support"],
    supportEmail: ["super_admin", "admin", "support"],
    viewSensitiveWill: ["super_admin", "admin", "legal_review"],
    impersonateUser: ["super_admin"],
  };

  if (!role) return action !== "deleteUser";
  return permissions[action]?.includes(role) ?? true;
};

export const getAdminRole = (user) => {
  return user?.role || user?.admin?.role || user?.user?.role || user?.data?.role || user?.adminDetails?.role || null;
};

export const formatAdminDate = (value, fallback = "—") => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatAdminDateTime = (value, fallback = "—") => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatLabel = (value, fallback = "—") => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return fallback;
  return String(value).replace(/_/g, " ");
};

export const cleanParams = (params) => {
  return Object.entries(params || {}).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "" && value !== "all") {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const getApiErrorMessage = (error) => {
  if (error?.response?.status === 403) {
    return "You do not have permission to perform this action.";
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong. Please try again."
  );
};

export const notifyApiError = (error) => {
  notification.error({
    message: "Request failed",
    description: getApiErrorMessage(error),
  });
};

export const toCsv = (rows) => {
  if (!rows?.length) return "";
  const keys = Object.keys(rows[0]);
  const escapeValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = typeof value === "object" ? JSON.stringify(value) : String(value);
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  return [keys.join(","), ...rows.map((row) => keys.map((key) => escapeValue(row[key])).join(","))].join("\n");
};

export const downloadCsv = (rows, filename) => {
  const csv = toCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const formatAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const value = Number(amount);
  if (Number.isNaN(value)) return amount;
  const normalized = value >= 1000 ? value / 100 : value;
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(normalized);
};
